import { Heading, HeadingLevel } from "@ariakit/react";
import { maybeAppendCopyToText } from "@indietabletop/appkit/append-copy-to-text";
import { classNames } from "@indietabletop/appkit/class-names";
import { useMediaQuery } from "@indietabletop/appkit/use-media-query";
import { RefObject, useRef } from "react";
import { useLocation } from "wouter";
import { Unit } from "../../domain/unit";
import { getId } from "../../lib/nanoid";
import {
  ActionsMenu,
  MenuDialogDisclosure,
  MenuDivider,
} from "../actions-menu";
import { atomic } from "../atomic.css";
import { ConfirmDialog } from "../confirm-dialog";
import { UnitConstraintsValidationBox } from "../constraints-validation";
import { ContentView } from "../content-view";
import { useDatabase } from "../database-provider";
import { DialogTrigger } from "../dialog-trigger";
import { Icon } from "../icon";
import { IconButton } from "../icon-button";
import { KeywordItem } from "../keyword-item";
import { DialogListButton } from "../list-button";
import { ListEndMarker } from "../list-end-marker";
import { MinWidth } from "../media";
import { Multiplier } from "../multiplier-counter";
import { Paragraphs } from "../paragraphs";
import { useRuleset } from "../ruleset-provider/context";
import { SubtleHeading } from "../subtle-heading";
import { UnitAbilitiesDialog } from "../unit-abilities-dialog";
import { UpdateUnitDialog } from "../unit-dialog";
import { UnitKeywordsDialog } from "../unit-keywords-dialog";
import * as css from "./style.css";

function UnitActionsMenu(props: {
  unit: Unit;
  editButtonRef: RefObject<HTMLDivElement | null>;
}) {
  const { unit, editButtonRef } = props;
  const [_, navigate] = useLocation();
  const ruleset = useRuleset();

  return (
    <ActionsMenu>
      <DialogTrigger>
        <UpdateUnitDialog unit={unit} />
        <MenuDialogDisclosure ref={editButtonRef}>Edit</MenuDialogDisclosure>
      </DialogTrigger>

      <DialogTrigger>
        <DuplicateUnitDialog unit={unit} />
        <MenuDialogDisclosure>Duplicate</MenuDialogDisclosure>
      </DialogTrigger>

      <MenuDivider />

      <DialogTrigger>
        <ConfirmDialog
          heading="Delete unit?"
          description={`Are you sure you want to delete "${unit.name}"?`}
          submitLabel="Delete"
          onSubmit={async () => {
            const { army } = unit;
            await army.ruleset.db.updateArmy({
              ...army.data,
              ruleset_version: ruleset.version,
              units: army.data.units.filter((u) => u.id !== unit.data.id),
            });
            navigate("/");
          }}
        />
        <MenuDialogDisclosure>Delete</MenuDialogDisclosure>
      </DialogTrigger>
    </ActionsMenu>
  );
}

function StatTable(props: { unit: Unit }) {
  const { unit } = props;
  const isWide = useMediaQuery(MinWidth.MEDIUM);

  if (!isWide) {
    return (
      <div className={atomic({ marginBlockStart: 32 })}>
        <table className={mobileTable}>
          <thead className={unitTableHead}>
            <tr>
              <th className={unitTableCell}>Cost</th>
              <th className={unitTableCell}>Range</th>
              <th className={unitTableCell}>Move</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={unitTableCell}>{unit.cost}pts</td>
              <td className={unitTableCell}>{unit.range}”</td>
              <td className={unitTableCell}>{unit.movement}”</td>
            </tr>
          </tbody>
        </table>

        <table
          className={classNames(mobileTable, atomic({ marginBlockStart: 12 }))}
        >
          <thead className={unitTableHead}>
            <tr>
              <th className={unitTableCell}>Combat</th>
              <th className={unitTableCell}>Defense</th>
              <th className={unitTableCell}>Morale</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={unitTableCell}>{unit.combat}+</td>
              <td className={unitTableCell}>{unit.defense}+</td>
              <td className={unitTableCell}>{unit.morale}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <table className={unitProfileTable}>
      <thead className={unitTableHead}>
        <tr>
          <th className={unitTableCell}>Cost</th>
          <th className={unitTableCell}>Range</th>
          <th className={unitTableCell}>Move</th>
          <th className={unitTableCell}>Combat</th>
          <th className={unitTableCell}>Defense</th>
          <th className={unitTableCell}>Morale</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className={unitTableCell}>{unit.cost}pts</td>
          <td className={unitTableCell}>{unit.range}”</td>
          <td className={unitTableCell}>{unit.movement}”</td>
          <td className={unitTableCell}>{unit.combat}+</td>
          <td className={unitTableCell}>{unit.defense}+</td>
          <td className={unitTableCell}>{unit.morale}</td>
        </tr>
      </tbody>
    </table>
  );
}

function DuplicateUnitDialog(props: { unit: Unit }) {
  const [_, navigate] = useLocation();
  const { unit } = props;
  const ruleset = useRuleset();

  return (
    <ConfirmDialog
      heading="Duplicate unit?"
      description="A new unit with identical configuration will be created."
      submitLabel="Duplicate"
      onSubmit={async () => {
        const { army } = unit;
        const unitId = getId();

        await army.ruleset.db.updateArmy({
          ...army.data,
          ruleset_version: ruleset.version,
          units: army.data.units.concat({
            ...unit.data,
            id: unitId,
            name: maybeAppendCopyToText(unit.data.name),
            created_ts: Date.now(),
            updated_ts: Date.now(),
          }),
        });

        navigate(`/u/${unitId}`);
      }}
    />
  );
}

const unitName = atomic({
  fontSize: 32,
  fontFamily: "barlow-condensed",
  fontWeight: "semi-bold",
});

const unitSummary = atomic({
  marginBlockStart: 6,
  fontWeight: "medium",
});

const unitProfileTable = atomic({
  fontWeight: "medium",
  tableLayout: "fixed",
  borderCollapse: "collapse",
  inlineSize: "container",
  textAlign: "left",
  marginBlockStart: 32,
});

const mobileTable = atomic({
  fontWeight: "medium",
  tableLayout: "fixed",
  borderCollapse: "collapse",
  inlineSize: "container",
  textAlign: "left",
});

const unitTableHead = atomic({
  borderBlockEnd: "dashed",
});

const unitTableCell = atomic({
  paddingBlock: 8,
});

export function UnitView(props: {
  unit: Unit;
  headerIcon: "back" | "cross";
  readOnly?: boolean;
}) {
  const { unit, readOnly } = props;

  const editButtonRef = useRef<HTMLDivElement | null>(null);
  const db = useDatabase();
  const ruleset = useRuleset();

  const removeKeyword = (keywordId: string) => {
    const data = unit.army.data;

    void db.updateArmy({
      ...data,
      ruleset_version: ruleset.version,
      units: data.units.map((unitData) => {
        if (unitData.id === unit.data.id) {
          return {
            ...unitData,
            keywords: unitData.keywords.filter((keywordRef) => {
              return keywordRef.id !== keywordId;
            }),
          };
        }

        return unitData;
      }),
    });
  };

  const removeAbility = (abilityId: string) => {
    const data = unit.army.data;

    void db.updateArmy({
      ...data,
      ruleset_version: ruleset.version,
      units: data.units.map((unitData) => {
        if (unitData.id === unit.data.id) {
          return {
            ...unitData,
            abilities: unitData.abilities.filter((abilityRef) => {
              return abilityRef.id !== abilityId;
            }),
          };
        }

        return unitData;
      }),
    });
  };

  const decrementUnitCount = () => {
    const data = unit.army.data;

    void db.updateArmy({
      ...data,
      ruleset_version: ruleset.version,
      units: data.units.map((unitData) => {
        if (unitData.id === unit.data.id) {
          return {
            ...unitData,
            count: Math.max(unitData.count - 1, 1),
          };
        }

        return unitData;
      }),
    });
  };

  const incrementUnitCount = () => {
    const data = unit.army.data;

    void db.updateArmy({
      ...data,
      ruleset_version: ruleset.version,
      units: data.units.map((unitData) => {
        if (unitData.id === unit.data.id) {
          return {
            ...unitData,
            count: unitData.count + 1,
          };
        }

        return unitData;
      }),
    });
  };

  return (
    <>
      <ContentView
        heading={!readOnly ? "Unit" : "Shared Unit"}
        icon={props.headerIcon}
        backHref="/"
        menu={
          !readOnly && (
            <UnitActionsMenu unit={unit} editButtonRef={editButtonRef} />
          )
        }
      >
        <div className={css.unitView}>
          <div>
            <Heading className={unitName}>
              <span onDoubleClick={() => editButtonRef.current?.click()}>
                {unit.name}
              </span>

              {unit.data.count > 1 && (
                <>
                  {" "}
                  <Multiplier count={unit.data.count} fontSize={20} />
                </>
              )}
            </Heading>
            <div className={unitSummary}>
              {unit.name !== unit.profile.name && <>{unit.profile.name}, </>}
              {unit.baseSize}
            </div>

            <StatTable unit={unit} />
          </div>

          {!readOnly && unit.data.count && (
            <div
              className={atomic({
                alignItems: "center",
                display: "flex",
                borderBlockStart: "solid",
                borderBlockEnd: "dashed",
                paddingBlock: 16,
              })}
            >
              <div
                style={{ flexGrow: 1 }}
                className={atomic({ fontWeight: "medium" })}
              >
                Unit count
              </div>

              {unit.data.count > 1 && (
                <IconButton
                  icon="back"
                  onClick={decrementUnitCount}
                  label="Decrement"
                />
              )}

              <div style={{ textAlign: "center", inlineSize: "3rem" }}>
                {unit.data.count}
              </div>

              <IconButton
                icon="forward"
                onClick={incrementUnitCount}
                label="Increment"
              />
            </div>
          )}

          {unit.data.description && (
            <Paragraphs
              text={unit.data.description}
              className={atomic({ marginBlockStart: 0 })}
            />
          )}

          {!unit.isValid && <UnitConstraintsValidationBox unit={unit} />}

          {((readOnly && unit.keywords.length > 0) || !readOnly) && (
            <HeadingLevel>
              <div>
                <SubtleHeading>Keywords</SubtleHeading>

                <div>
                  {unit.keywords.length > 0 && (
                    <ul className={atomic({ borderBlockStart: "solid" })}>
                      {unit.keywords.map((keyword) => {
                        const isDouble = keyword.ref.count > 1;
                        const isInheritedFromArmy =
                          unit.army.keyword?.id === keyword.id;

                        // prettier-ignore
                        const summary = isInheritedFromArmy ?
                          // If keyword is inherit from the army, we make sure to
                          // show that. Additionally, if it is *also* purchased
                          // for the unit, show the price.
                          `Army keyword` + (isDouble ? `, ${keyword.cost}pts` : ``) :

                          // Otherwise show the keyword cost, unless the cost
                          // is 0, in which case render the word Free.
                          keyword.cost === 0 ? `Free` : `${keyword.cost}pts`;

                        return (
                          <KeywordItem
                            key={keyword.id}
                            tag="li"
                            name={keyword.name}
                            count={keyword.ref.count}
                            summary={
                              <>
                                {summary}
                                {" · "}
                                {keyword.definition.description}
                              </>
                            }
                            icon={
                              readOnly ? null : isInheritedFromArmy ? (
                                <Icon name="locked" />
                              ) : (
                                <IconButton
                                  icon="cross"
                                  label="Remove keyword"
                                  onClick={() => {
                                    removeKeyword(keyword.id);
                                  }}
                                />
                              )
                            }
                          />
                        );
                      })}
                    </ul>
                  )}
                  {!readOnly ? (
                    <DialogTrigger>
                      <UnitKeywordsDialog {...props} />
                      <DialogListButton>Add keywords</DialogListButton>
                    </DialogTrigger>
                  ) : (
                    <ListEndMarker />
                  )}
                </div>
              </div>
            </HeadingLevel>
          )}

          {((readOnly && unit.abilities.length > 0) || !readOnly) && (
            <HeadingLevel>
              <div>
                <SubtleHeading>Abilities</SubtleHeading>

                <div>
                  {unit.abilities.length > 0 && (
                    <ul className={atomic({ borderBlockStart: "solid" })}>
                      {unit.abilities.map((ability) => {
                        return (
                          <KeywordItem
                            key={ability.id}
                            tag="li"
                            name={ability.name}
                            summary={ability.summary}
                            icon={
                              !readOnly && (
                                <IconButton
                                  label="Remove ability"
                                  icon="cross"
                                  onClick={() => {
                                    removeAbility(ability.id);
                                  }}
                                />
                              )
                            }
                          />
                        );
                      })}
                    </ul>
                  )}

                  {!readOnly ? (
                    <DialogTrigger>
                      <UnitAbilitiesDialog {...props} />
                      <DialogListButton>Add abilities</DialogListButton>
                    </DialogTrigger>
                  ) : (
                    <ListEndMarker />
                  )}
                </div>
              </div>
            </HeadingLevel>
          )}

          {readOnly &&
            unit.keywords.length === 0 &&
            unit.abilities.length === 0 && (
              <div
                className={atomic({
                  textAlign: "center",
                  fontStyle: "italic",
                  fontSize: 14,
                  paddingBlock: 24,
                })}
              >
                This unit has no keywords or special abilities
              </div>
            )}
        </div>
      </ContentView>
    </>
  );
}
